// Login.jsx — v3.0 : identités dérivées de passphrase (modèle portefeuille).
// - Création : passphrase de 9 mots GÉNÉRÉE (défaut, ~72 bits) ou personnalisée
//   (≥ 12 caractères, déconseillée), affichée UNE SEULE FOIS avec confirmation
//   obligatoire. La paire X25519 est dérivée (Argon2id) : rien n'est stocké
//   nulle part, pseudo + passphrase régénèrent l'identité sur tout appareil.
// - Récupération : pseudo + passphrase → dérivation → comparaison avec la clé
//   publique de l'annuaire. L'import de fichier (v1.1) reste en secours.
import React, { useState } from 'react';
import {
  fingerprint, importIdentity, generatePassphrase, deriveKeyPairFromPassphrase,
} from '../crypto.js';

export default function Login({ onDone }) {
  const [mode, setMode] = useState('create'); // create | recover | import
  const [pseudo, setPseudo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [customPass, setCustomPass] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [created, setCreated] = useState(null); // { identity, fp, passphrase }
  const [confirmed, setConfirmed] = useState(false);
  const [recoverPass, setRecoverPass] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importPass, setImportPass] = useState('');

  async function create(e) {
    e.preventDefault();
    setError('');
    if (useCustom && customPass.trim().length < 12) {
      return setError('Passphrase personnalisée : 12 caractères minimum (la passphrase générée reste plus sûre).');
    }
    setBusy(true);
    try {
      const passphrase = useCustom ? customPass.trim() : generatePassphrase();
      // Dérivation Argon2id : peut prendre ~1 s, volontairement coûteux.
      const keys = deriveKeyPairFromPassphrase(pseudo, passphrase);
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo, publicKey: keys.publicKey }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Inscription impossible.');
      const identity = { pseudo, ...keys, secured: true };
      setCreated({ identity, fp: fingerprint(keys.publicKey), passphrase });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function recover(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const keys = deriveKeyPairFromPassphrase(pseudo, recoverPass);
      const res = await fetch(`/api/users/${encodeURIComponent(pseudo.trim())}`);
      if (!res.ok) throw new Error('Pseudo introuvable (compte purgé pour inactivité ?).');
      const u = await res.json();
      if (u.publicKey !== keys.publicKey) {
        throw new Error('Passphrase incorrecte pour ce pseudo (ou compte non encore sécurisé par passphrase — utilisez alors l’import de fichier).');
      }
      onDone({ pseudo: u.pseudo, ...keys, secured: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function doImport(e) {
    e.preventDefault();
    setError('');
    if (!importFile) return;
    setBusy(true);
    try {
      const identity = importIdentity(await importFile.text(), importPass);
      const res = await fetch(`/api/users/${encodeURIComponent(identity.pseudo)}`);
      if (!res.ok) throw new Error('Ce compte n\u2019existe plus (purgé pour inactivité ?).');
      const u = await res.json();
      if (u.publicKey !== identity.publicKey) throw new Error('La clé de ce compte a changé (sécurisé par passphrase entre-temps ? utilisez la récupération par passphrase).');
      onDone(identity);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // --- Écran d'affichage unique de la passphrase ---
  if (created) {
    return (
      <div className="center">
        <div className="card">
          <h1>Compte créé : {created.identity.pseudo}</h1>
          <p className="small"><strong>Votre passphrase de récupération — affichée UNE SEULE FOIS :</strong></p>
          <p className="fingerprint">{created.passphrase}</p>
          <button type="button" className="ghost"
                  onClick={() => navigator.clipboard?.writeText(created.passphrase)}>
            📋 Copier la passphrase
          </button>
          <p className="muted small">
            Notez-la sur papier ou dans un gestionnaire de mots de passe. Comme
            pour un portefeuille crypto : <strong>pseudo + passphrase = votre
            compte</strong>, sur n'importe quel appareil, même si ce navigateur
            efface ses données. Perdue = compte définitivement irrécupérable
            (personne, pas même le serveur, ne peut la retrouver).
          </p>
          <p className="muted small">Votre empreinte publique : <span className="fingerprint inline">{created.fp}</span></p>
          <label className="row" style={{ alignItems: 'center' }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={confirmed}
                   onChange={(e) => setConfirmed(e.target.checked)} />
            <span className="small">J'ai enregistré ma passphrase en lieu sûr</span>
          </label>
          <button disabled={!confirmed} onClick={() => onDone(created.identity)}>Continuer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <div className="card">
        <h1>Messagerie chiffrée</h1>
        <div className="row">
          <button type="button" className={mode === 'create' ? '' : 'ghost'} onClick={() => { setMode('create'); setError(''); }}>Créer</button>
          <button type="button" className={mode === 'recover' ? '' : 'ghost'} onClick={() => { setMode('recover'); setError(''); }}>Récupérer</button>
          <button type="button" className={mode === 'import' ? '' : 'ghost'} onClick={() => { setMode('import'); setError(''); }}>Fichier</button>
        </div>

        {mode === 'create' && (
          <form onSubmit={create} className="group-form">
            <p className="muted small">
              Choisissez un pseudo — aucune autre information n'est demandée. Une
              passphrase de récupération (type portefeuille crypto) sera générée.
              Comptes inactifs plus de 7 jours supprimés automatiquement.
            </p>
            <input autoFocus value={pseudo} onChange={(e) => setPseudo(e.target.value)}
                   placeholder="Pseudo (3–32 caractères)" pattern="[a-zA-Z0-9_\-]{3,32}" required />
            <label className="row" style={{ alignItems: 'center' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={useCustom}
                     onChange={(e) => setUseCustom(e.target.checked)} />
              <span className="small">Choisir ma propre passphrase (déconseillé : la générée est plus sûre)</span>
            </label>
            {useCustom && (
              <input type="password" value={customPass} onChange={(e) => setCustomPass(e.target.value)}
                     placeholder="Passphrase personnalisée (12 caractères min.)" />
            )}
            {error && <p className="error">{error}</p>}
            <button disabled={busy}>{busy ? 'Dérivation des clés…' : 'Créer mon compte'}</button>
          </form>
        )}

        {mode === 'recover' && (
          <form onSubmit={recover} className="group-form">
            <p className="muted small">
              Retrouvez votre compte sur n'importe quel appareil avec votre pseudo
              et votre passphrase. L'historique des messages, lui, reste propre à
              chaque appareil (jamais en ligne).
            </p>
            <input value={pseudo} onChange={(e) => setPseudo(e.target.value)} placeholder="Pseudo" required />
            <input type="password" value={recoverPass} onChange={(e) => setRecoverPass(e.target.value)}
                   placeholder="Passphrase" required />
            {error && <p className="error">{error}</p>}
            <button disabled={busy}>{busy ? 'Dérivation…' : 'Récupérer mon compte'}</button>
          </form>
        )}

        {mode === 'import' && (
          <form onSubmit={doImport} className="group-form">
            <p className="muted small">
              Ancien mode (fichier d'identité exporté). Toujours accepté, notamment
              pour les comptes pas encore sécurisés par passphrase.
            </p>
            <input type="file" accept=".json,application/json" required
                   onChange={(e) => setImportFile(e.target.files[0])} />
            <input type="password" value={importPass} placeholder="Passphrase du fichier"
                   onChange={(e) => setImportPass(e.target.value)} required />
            {error && <p className="error">{error}</p>}
            <button disabled={busy}>{busy ? 'Déchiffrement…' : 'Importer'}</button>
          </form>
        )}
      </div>
    </div>
  );
}
